import { useState, useCallback, useEffect, useMemo } from "react"
import { Form, message, Space } from "antd"
import { Drawer } from "src/global/ui/Drawer/Drawer.tsx"
import { useMutation, useQuery, useApolloClient } from "@apollo/client"
import { LoadingState } from "src/global/ui/LoadingState"
import { graphql } from "src/api/graphql/gql.ts"
import {
  SaveExpectationFooter,
  GenerateSqlFooter,
} from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/CreateExpectationDrawerFooters.tsx"
import { SelectedExpectation } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/types.ts"
import {
  ExpectationTitle,
  SnakeCasedExpectationType,
} from "src/global/schemas/expectations/expectation-metadata-utils.ts"
import { handleWindowedPayload } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/windowedExpectationUtils.ts"
import { handleRowConditionPayload } from "src/pages/DataAssets/views/Expectations/Expectation/uiForms/customRenderers/RowConditionControl/rowConditionParser.ts"
import {
  ExpectationsTab_GetExpectationsDocument,
  ExpectationsTabDataAssetDocument,
  AssetCoverageStatsDocument,
} from "src/pages/DataAssets/views/Expectations/SimpleExpectationsTab"

import { CREATE_EXPECTATION_SUCCESS } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/words.ts"
import { MESSAGE_DURATION_SECONDS } from "src/global/config.ts"
import { CreateExpectationDrawerAlert } from "src/global/components/alerts/DemoData/CreateExpectationDrawerAlert.tsx"
import { useIsDemoData } from "src/global/hooks/useIsDemoData.ts"
import { theme } from "src/global/ui/themes/theme.ts"
import { useDemoDataAssetName } from "src/global/hooks/useDemoDataAssetName.ts"
import { DataQualityIssueBasedPicker } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/DataQualityIssueBasedPicker/DataQualityIssueBasedPicker.tsx"
import { useSelectExpectation } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/useSelectExpectation.ts"
import { CreateExpectationFeedbackButton } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/CreateExpectationFeedback.tsx"
import { AggregateCoverageStatsDocument } from "src/pages/DataAssets/components/AggregateAssetCoverageStats"
import { ExpectationDrawerEditor } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/ExpectationDrawerEditor.tsx"
import { useIsFeatureEnabled } from "src/global/hooks/useIsFeatureEnabled"
import {
  isDataQualityIssueFullyCovered,
  isAnomalyDetectionQualityIssue,
} from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/AutomaticExpectationEditor/AutomaticExpectationEditor"
import { upperCase } from "lodash-es"
import { DataQualityIssues } from "src/api/graphql/graphql.ts"
import { useEventAction } from "src/global/hooks/useEventAction"
import { EventTypes } from "src/types/events"
import { parseErrorMessage } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/parseErrorMessage"

export const SEGMENT_VALUES = {
  AUTOMATIC: "automatic",
  MANUAL: "manual",
  GENERATE_SQL: "generate_sql",
  WRITE_OWN_SQL: "write_own_sql",
} as const
export type SegmentValue = (typeof SEGMENT_VALUES)[keyof typeof SEGMENT_VALUES]

import {
  isExpectAiAllowedExpectation,
  isExpectAiSupportedDataSource,
} from "src/pages/DataAssets/views/Expectations/utils/expectAiUtils.ts"
import { DEFAULT_METRICS } from "src/pages/DataAssets/views/AssetDetails/MetricsTable"

export const NewExpectationDrawer_DataAssetDocument = graphql(`
  query newExpectationDrawer_DataAsset($id: UUID!, $metricNames: [MetricType!]!) {
    dataAsset(id: $id) {
      id
      __typename
      splitter {
        ... on WithID {
          id
        }
      }
      datasourceV2 {
        id
        type
      }
      ...ExpectationEditor_DataAssetMetricRun
      ...UseIsDemoData_DataAsset
      ...UseDemoDataAssetName_DataAsset
    }
  }
`)

const AddExpectationToAssetDocument = graphql(`
  mutation addExpectationToAsset($input: AddExpectationToDataAssetInput!) {
    addExpectationToDataAsset(input: $input) {
      expectation {
        geCloudId
      }
    }
  }
`)

const CreateAutoGenerateExpectationsJobDocument = graphql(`
  mutation CreateAutoGenerateExpectationsJob($input: CreateAutoGenerateExpectationsJobInput!) {
    createAutoGenerateExpectationsJob(input: $input) {
      jobId
    }
  }
`)

export const newExpectationDrawer_fetchAssetSchemaDocument = graphql(`
  mutation createMetricsListJob($dataAssetId: UUID!, $metricList: [MetricType]!) {
    createMetricsListJob(dataAssetId: $dataAssetId, metricList: $metricList) {
      jobId
    }
  }
`)

export const AutomaticExpectationEditorCoverageStatsDocument = graphql(`
  query AnomalyDetectionExpectationEditorCoverageStats($id: UUID!) {
    dataAsset(id: $id) {
      id
      coverageStats {
        anomalyDetection {
          hasSchemaCoverage
          hasVolumeCoverage
          hasCompletenessCoverage
          numColumnsWithCompletenessCoverage
          totalColumnCount
        }
      }
    }
  }
`)

export const CreateGenerateExpectationJobDocument = graphql(`
  mutation CreateGenerateExpectationJob($input: CreateGenerateExpectationJobInput!) {
    createGenerateExpectationJob(input: $input) {
      jobId
    }
  }
`)

// eslint-disable-next-line no-restricted-imports -- deprecated file
import { ExpectationSuitesDocument, JobFragment } from "src/api/graphql/graphql-operations.ts"
import { JobStateObserver } from "src/global/components/JobStateObserver.tsx"

type Props = {
  open: boolean
  close: () => void
  dataAssetId: string
  onExpectationCreated?: (expectationId: string) => void
}

export function SimpleNewExpectationDrawer({ open, dataAssetId, close, onExpectationCreated }: Props) {
  const [drawerPage, setDrawerPage] = useState<DrawerPage>("1 Expectation Picker")
  const [hasRequestedMetricsJob, setHasRequestedMetricsJob] = useState(false)
  const [promptText, setPromptText] = useState("")

  // SQL Generation loading state management
  const [isGeneratingExpectation, setIsGeneratingExpectation] = useState(false)
  const [generateSqlJobId, setGenerateSqlJobId] = useState<string | null>(null)
  const [expectAiError, setExpectAiError] = useState<string | null>(null)
  const [expectationIdsBeforeJob, setExpectationIdsBeforeJob] = useState<string[]>([])
  const apollo = useApolloClient()

  const { data: anomalyDetectionCoverageStats, refetch: refetchAutomaticCoverageStats } = useQuery(
    AutomaticExpectationEditorCoverageStatsDocument,
    {
      variables: {
        id: dataAssetId,
      },
      skip: drawerPage === "1 Expectation Picker", // Only run query on the expectation picker page
    },
  )

  /*
   * Refetch the anomaly detection coverage stats when a new metric run is created
   */
  useEventAction(EventTypes.METRIC_RUN.CREATED, () => {
    refetchAutomaticCoverageStats()
  })

  const [createMetricsListJob] = useMutation(newExpectationDrawer_fetchAssetSchemaDocument)

  /*
   * Run job to fetch the latest column metrics for the Asset
   */
  useEffect(() => {
    if (dataAssetId && drawerPage === "1 Expectation Picker" && open === true && !hasRequestedMetricsJob) {
      createMetricsListJob({
        variables: {
          dataAssetId: dataAssetId,
          metricList: ["TABLE_COLUMNS"],
        },
      })
      setHasRequestedMetricsJob(true)
    }
  }, [dataAssetId, createMetricsListJob, drawerPage, open, hasRequestedMetricsJob])

  const {
    expectationConfiguration,
    selectedExpectation,
    selectedDataQualityIssue,
    resetSelectionState,
    selectExpectation,
    handleConfigChange,
  } = useSelectExpectation()

  const { data, loading: dataLoading } = useQuery(NewExpectationDrawer_DataAssetDocument, {
    variables: {
      id: dataAssetId,
      metricNames: DEFAULT_METRICS,
    },
    skip: !dataAssetId,
  })

  const [userSelectedSegment, setUserSelectedSegment] = useState<SegmentValue | null>(null)

  const generateSqlExpectationEnabled = useIsFeatureEnabled("generateSqlExpectationEnabled")
  const generateSqlDraftConfigEnabled = useIsFeatureEnabled("generateSqlDraftConfigEnabled")
  const forceAgentEnabled = useIsFeatureEnabled("forceEnableAgent")
  const expectAiEnabled = useIsFeatureEnabled("expectAiEnabled")

  const isExpectAiEligible = useMemo(() => {
    return Boolean(
      !dataLoading &&
        data?.dataAsset?.datasourceV2?.type &&
        generateSqlExpectationEnabled &&
        !forceAgentEnabled &&
        expectAiEnabled &&
        selectedExpectation?.value &&
        isExpectAiAllowedExpectation(selectedExpectation.value) &&
        isExpectAiSupportedDataSource(data.dataAsset.datasourceV2.type),
    )
  }, [
    dataLoading,
    data?.dataAsset?.datasourceV2?.type,
    generateSqlExpectationEnabled,
    forceAgentEnabled,
    expectAiEnabled,
    selectedExpectation?.value,
  ])

  const initialSegment = useMemo((): SegmentValue => {
    if (isAnomalyDetectionQualityIssue(selectedDataQualityIssue)) {
      return SEGMENT_VALUES.AUTOMATIC
    }
    if (generateSqlDraftConfigEnabled) {
      return SEGMENT_VALUES.WRITE_OWN_SQL
    }
    if (isExpectAiEligible) {
      return SEGMENT_VALUES.GENERATE_SQL
    }
    return SEGMENT_VALUES.MANUAL
  }, [selectedDataQualityIssue, generateSqlDraftConfigEnabled, isExpectAiEligible])

  const activeSegment = userSelectedSegment ?? initialSegment

  const [
    createExpectationMutation,
    { loading: loadingCreateManualExpectation, error: addExpectationToAssetError, reset: resetCreateManualExpectation },
  ] = useMutation(AddExpectationToAssetDocument, {
    refetchQueries: [
      { query: ExpectationsTab_GetExpectationsDocument, variables: { input: { dataAssetId } } },
      { query: ExpectationsTabDataAssetDocument, variables: { id: dataAssetId as string } },
      AssetCoverageStatsDocument,
      AggregateCoverageStatsDocument,
    ],
  })

  const [
    createAutomaticExpectationMutation,
    {
      loading: loadingCreateAutomaticExpectation,
      error: addAutomaticExpectationToAssetError,
      reset: resetCreateAutomaticExpectation,
    },
  ] = useMutation(CreateAutoGenerateExpectationsJobDocument, {
    refetchQueries: [
      { query: ExpectationsTab_GetExpectationsDocument, variables: { input: { dataAssetId } } },
      { query: ExpectationsTabDataAssetDocument, variables: { id: dataAssetId as string } },
      AssetCoverageStatsDocument,
      AggregateCoverageStatsDocument,
    ],
  })

  const [
    createGenerateExpectationMutation,
    { loading: loadingCreateGenerateExpectation, reset: resetCreateGenerateExpectation },
  ] = useMutation(CreateGenerateExpectationJobDocument, {
    refetchQueries: [
      { query: ExpectationsTab_GetExpectationsDocument, variables: { input: { dataAssetId } } },
      { query: ExpectationsTabDataAssetDocument, variables: { id: dataAssetId as string } },
      AssetCoverageStatsDocument,
      AggregateCoverageStatsDocument,
    ],
  })

  const [form] = Form.useForm()

  const goBackToPageOne = useCallback(() => {
    setDrawerPage("1 Expectation Picker")
    setUserSelectedSegment(null)
    // Don't reset form data - preserve user's work when navigating back
  }, [])

  const resetState = useCallback(() => {
    setDrawerPage("1 Expectation Picker")
    resetSelectionState()
    resetCreateManualExpectation()
    resetCreateAutomaticExpectation()
    resetCreateGenerateExpectation()
    setPromptText("")
    form.resetFields()
    // Reset SQL generation state
    setIsGeneratingExpectation(false)
    setGenerateSqlJobId(null)
    setExpectAiError(null)
    setExpectationIdsBeforeJob([])
  }, [
    resetCreateManualExpectation,
    resetCreateAutomaticExpectation,
    resetCreateGenerateExpectation,
    resetSelectionState,
    form,
  ])

  const handleClose = useCallback(() => {
    close()
    resetState()
    resetSelectionState()
  }, [close, resetState, resetSelectionState])

  const handleGenerateSqlJobComplete = useCallback(
    (job: JobFragment) => {
      setIsGeneratingExpectation(false)
      setGenerateSqlJobId(null)

      // Check if there's an error message even though status is "complete"
      if (job.errorMessage) {
        const cleanErrorMessage = parseErrorMessage(job.errorMessage)
        setExpectAiError(cleanErrorMessage)
        return
      }

      // Look for the created expectation in job.createdResources
      const createdExpectationId = job.createdResources?.find(
        (resource) => resource.entityType === "Expectation",
      )?.entityId

      if (createdExpectationId) {
        // Refetch expectations to get the latest data
        apollo.refetchQueries({
          include: [
            ExpectationsTab_GetExpectationsDocument,
            ExpectationsTabDataAssetDocument,
            AssetCoverageStatsDocument,
            AggregateCoverageStatsDocument,
          ],
        })

        // Show success message and close this drawer
        message.success("SQL Expectation generated successfully", MESSAGE_DURATION_SECONDS)
        handleClose()

        // Open the edit drawer for the newly created expectation
        onExpectationCreated?.(createdExpectationId)
      } else {
        // The backend job is working but not reporting createdResources properly
        // Always refetch and show success since the expectation appears after refresh
        apollo
          .refetchQueries({
            include: [
              ExpectationsTab_GetExpectationsDocument,
              ExpectationsTabDataAssetDocument,
              AssetCoverageStatsDocument,
              AggregateCoverageStatsDocument,
            ],
          })
          .then(() => {
            // Try to get the refreshed expectations list from cache
            const refreshedData = apollo.cache.readQuery({
              query: ExpectationsTab_GetExpectationsDocument,
              variables: { input: { dataAssetId } },
            })

            message.success("SQL Expectation generated successfully", MESSAGE_DURATION_SECONDS)
            if (refreshedData?.expectations?.length && expectationIdsBeforeJob.length > 0) {
              // Find the expectation that wasn't in the original list before the job started
              const newExpectation = refreshedData.expectations.find(
                (exp) => exp.geCloudId && !expectationIdsBeforeJob.includes(exp.geCloudId),
              )

              if (newExpectation?.geCloudId) {
                // Show success message and close this drawer
                handleClose()

                // Try to open the edit drawer for the newly created expectation
                onExpectationCreated?.(newExpectation.geCloudId)

                // Clean up state after successful completion
                setExpectationIdsBeforeJob([])
              } else {
                handleClose()

                // Clean up state even if we can't find the specific expectation
                setExpectationIdsBeforeJob([])
              }
            } else {
              handleClose()
            }
          })
          .catch(() => {
            setExpectAiError("Expectation may have been created but failed to refresh the list")
          })
      }
    },
    [apollo, handleClose, onExpectationCreated, expectationIdsBeforeJob, dataAssetId],
  )

  const handleGenerateSqlJobError = useCallback((job: JobFragment) => {
    setIsGeneratingExpectation(false)
    setGenerateSqlJobId(null)
    const errorMessage = job.errorMessage || "Failed to generate SQL Expectation"
    const cleanErrorMessage = parseErrorMessage(errorMessage)
    setExpectAiError(cleanErrorMessage)
    setExpectationIdsBeforeJob([])
  }, [])

  const saveExpectation = useCallback(
    async (addMore: boolean | undefined) => {
      try {
        await form.validateFields()

        if (!selectedExpectation?.value) return // compiler assurance
        if (
          activeSegment === SEGMENT_VALUES.AUTOMATIC &&
          anomalyDetectionCoverageStats?.dataAsset?.coverageStats?.anomalyDetection
        ) {
          await createAutomaticExpectationMutation({
            variables: {
              input: {
                dataAssetId: dataAssetId,
                selectedDataQualityIssues: [upperCase(selectedDataQualityIssue) as DataQualityIssues],
              },
            },
          })
        } else {
          await createExpectationMutation({
            variables: {
              input: {
                dataAssetId,
                expectationConfiguration: JSON.stringify(
                  handleRowConditionPayload(
                    handleWindowedPayload(
                      {
                        ...expectationConfiguration,
                        expectation_type: selectedExpectation.value,
                      },
                      selectedExpectation.value as SnakeCasedExpectationType,
                      form,
                    ),
                  ),
                ),
              },
            },
          })
        }

        message.success(CREATE_EXPECTATION_SUCCESS, MESSAGE_DURATION_SECONDS)
        if (addMore) {
          resetState()
        } else {
          handleClose()
        }
      } catch {
        // nothing to do here; form validation will render error text as feedback
      }
    },
    [
      form,
      selectedExpectation?.value,
      activeSegment,
      anomalyDetectionCoverageStats?.dataAsset?.coverageStats?.anomalyDetection,
      resetState,
      handleClose,
      createAutomaticExpectationMutation,
      dataAssetId,
      selectedDataQualityIssue,
      createExpectationMutation,
      expectationConfiguration,
    ],
  )

  // temporary solution until GX-1127
  const { data: expectationSuitesData } = useQuery(ExpectationSuitesDocument, {
    variables: {
      options: {
        filterByAssetRefId: dataAssetId,
      },
    },
    skip: !dataAssetId,
  })

  const generateSqlExpectation = useCallback(async () => {
    try {
      // Validate form first - if this fails, we want to return early without showing error
      await form.validateFields()
    } catch {
      // Form validation failed - don't proceed and don't show any error alert
      // The form will already show the validation error messages
      return
    }

    try {
      const batchDefinitionId = data?.dataAsset?.splitter?.id
      if (!batchDefinitionId) {
        setExpectAiError("No batch definition available for this data asset")
        return
      }

      const expectationSuiteId = expectationSuitesData?.expectationSuitesV2?.[0]?.id
      if (!expectationSuiteId) {
        setExpectAiError("No expectation suite found for this data asset")
        return
      }

      // Store current expectation IDs before starting the job
      const currentExpectations = apollo.cache.readQuery({
        query: ExpectationsTab_GetExpectationsDocument,
        variables: { input: { dataAssetId } },
      })
      const currentExpectationIds =
        currentExpectations?.expectations?.map((exp) => exp.geCloudId).filter((id): id is string => Boolean(id)) || []

      setIsGeneratingExpectation(true)
      setExpectAiError(null)
      setGenerateSqlJobId(null)
      setExpectationIdsBeforeJob(currentExpectationIds)

      const { data: jobData } = await createGenerateExpectationMutation({
        variables: {
          input: {
            batchDefinitionId,
            expectationSuiteId,
            userPrompt: promptText,
          },
        },
      })

      const jobId = jobData?.createGenerateExpectationJob?.jobId || null
      setGenerateSqlJobId(jobId)

      if (!jobId) {
        setIsGeneratingExpectation(false)
        setExpectAiError("No job ID returned from server")
      }
    } catch (error) {
      setIsGeneratingExpectation(false)
      // Extract the actual error message from GraphQL error
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? String(error.message)
          : "Failed to start SQL generation job"

      // Parse the error message to extract just the detail if it's a DomainError format
      const cleanErrorMessage = parseErrorMessage(errorMessage)
      setExpectAiError(cleanErrorMessage)
    }
  }, [
    form,
    data?.dataAsset?.splitter?.id,
    promptText,
    createGenerateExpectationMutation,
    expectationSuitesData,
    apollo,
    dataAssetId,
  ])

  const onSelectExpectation = (expectation: SelectedExpectation) => {
    selectExpectation(expectation)
    setDrawerPage("2 Expectation Editor")
  }
  const isDemoData = useIsDemoData(data?.dataAsset)
  const assetName = useDemoDataAssetName(data?.dataAsset)
  const selectedDataQualityIssueMessage = selectedDataQualityIssue ? ` | ${selectedDataQualityIssue}` : ``
  const isSaveButtonDisabledForAutomatic = isDataQualityIssueFullyCovered(
    selectedDataQualityIssue,
    anomalyDetectionCoverageStats?.dataAsset?.coverageStats?.anomalyDetection,
  )

  return (
    <>
      {generateSqlJobId && (
        <JobStateObserver
          jobId={generateSqlJobId}
          onComplete={handleGenerateSqlJobComplete}
          onError={handleGenerateSqlJobError}
        />
      )}
      <Drawer
        title={`New Expectation${selectedDataQualityIssueMessage}`}
        placement="right"
        size="large"
        footer={
          <Footer
            drawerPage={drawerPage}
            onSave={saveExpectation}
            onBack={goBackToPageOne}
            onValidateAndClose={generateSqlExpectation}
            loading={loadingCreateManualExpectation || loadingCreateAutomaticExpectation || isGeneratingExpectation}
            disabled={activeSegment === SEGMENT_VALUES.AUTOMATIC && isSaveButtonDisabledForAutomatic}
            selectedSegment={activeSegment}
            isGeneratingExpectation={isGeneratingExpectation}
            isExpectAiEligible={isExpectAiEligible}
            generateSqlDraftConfigEnabled={generateSqlDraftConfigEnabled}
          />
        }
        destroyOnClose
        open={open}
        onClose={handleClose}
        extra={<CreateExpectationFeedbackButton />}
      >
        {isDemoData && (
          <Space direction="vertical" style={{ marginBottom: theme.spacing.xs }}>
            <CreateExpectationDrawerAlert
              assetName={assetName}
              page={drawerPage === "1 Expectation Picker" ? "picker" : "editor"}
              selectedExpectationTitle={selectedExpectation?.title as ExpectationTitle}
            />
          </Space>
        )}
        {drawerPage === "1 Expectation Picker" && (
          <DataQualityIssueBasedPicker onSelectExpectation={onSelectExpectation} />
        )}
        {drawerPage === "2 Expectation Editor" && selectedExpectation?.title ? (
          <ExpectationDrawerEditor
            form={form}
            selectedDataQualityIssue={selectedDataQualityIssue}
            onSelectExpectation={onSelectExpectation}
            selectedExpectation={selectedExpectation}
            expectationConfiguration={expectationConfiguration}
            handleConfigChange={handleConfigChange}
            dataAsset={data?.dataAsset ?? undefined}
            addExpectationToAssetError={addExpectationToAssetError}
            addAutomaticExpectationToAssetError={addAutomaticExpectationToAssetError}
            anomalyDetectionCoverageStats={anomalyDetectionCoverageStats?.dataAsset?.coverageStats?.anomalyDetection}
            activeSegment={activeSegment}
            setUserSelectedSegment={setUserSelectedSegment}
            promptText={promptText}
            setPromptText={setPromptText}
            isGeneratingExpectation={isGeneratingExpectation}
            expectAiError={expectAiError}
            isExpectAiEligible={isExpectAiEligible}
            generateSqlDraftConfigEnabled={generateSqlDraftConfigEnabled}
          />
        ) : (
          <LoadingState
            loading={
              loadingCreateManualExpectation ||
              loadingCreateAutomaticExpectation ||
              loadingCreateGenerateExpectation ||
              isGeneratingExpectation
            }
          />
        )}
      </Drawer>
    </>
  )
}

type DrawerPage = "1 Expectation Picker" | "2 Expectation Editor"
type FooterProps = {
  drawerPage: DrawerPage
  onBack: () => void
  onSave: (addMore?: boolean) => void
  onValidateAndClose: () => Promise<void>
  loading: boolean
  disabled: boolean
  selectedSegment: SegmentValue
  isGeneratingExpectation: boolean
  isExpectAiEligible: boolean
  generateSqlDraftConfigEnabled: boolean
}

function Footer({
  drawerPage,
  onBack,
  onSave,
  onValidateAndClose,
  loading,
  disabled,
  selectedSegment,
  isGeneratingExpectation,
  isExpectAiEligible,
  generateSqlDraftConfigEnabled,
}: FooterProps) {
  if (drawerPage === "2 Expectation Editor") {
    if (!generateSqlDraftConfigEnabled && isExpectAiEligible && selectedSegment === SEGMENT_VALUES.GENERATE_SQL) {
      return <GenerateSqlFooter onBack={onBack} onGenerateSql={onValidateAndClose} disabled={isGeneratingExpectation} />
    }

    return (
      <SaveExpectationFooter
        onBack={onBack}
        onSave={onSave}
        onSaveAndAdd={() => {
          onSave(true)
        }}
        saveLoading={loading}
        saveDisabled={disabled}
      />
    )
  }
  return null
}
